/* ---------------------------------------------------------------------------
   The inventory sheet.

   The one surface in the app that is a document rather than a panel: opaque,
   generously margined, ruled with hairlines, and laid out in numbered sections
   the way a DoR bridge-inventory sheet is. It prints — see the print rules at
   the foot of app.css, which drop the chrome and let this run onto paper.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import * as api from '../core/api.ts';
import type { State } from '../core/store.ts';
import { slotImage } from '../components/imageSlot.ts';
import { emptyState } from '../components/widgets.ts';
import { paper } from '../components/paper.ts';
import {
  cycleLabel,
  findBridge,
  scaleMax,
  inspectionStanding,
  inspectionTypeLabel,
  longDate,
  prettyDate,
} from '../data/derive.ts';

import { elementLabel } from '../data/elements.ts';
import { defectCodeLabel, defectPhotoCount } from '../data/defects.ts';
import { bandLabel, screenBridge } from '../data/screening.ts';
import { consequenceFacts } from '../data/consequence.ts';
import { provenanceOf } from '../data/provenance.ts';
import type { BridgeView } from '../data/types.ts';
import { agency } from '../tenant/agency.ts';
import { count, join, metres, NOT_RECORDED, phrase, shown } from '../data/recorded.ts';
import { vocab } from '../tenant/agency.ts';
import { chainage } from '../tenant/format.ts';

/* --------------------------- field lists --------------------------- */

function locationRows(b: BridgeView): [string, string][] {
  return [
    ['Asset id', b.id],
    ['Inventory code', b.inventoryCode ?? '—'],
    ['Road code', shown(b.route)],
    ['Road name', b.roadName],
    ['Road length', b.roadLength != null ? `${b.roadLength.toFixed(2)} km` : NOT_RECORDED],
    /* THE SHEET IS THE PAPER RECORD, so its row labels are the tenant's own —
       "Chainage / km 12.340" at DoR and "Station / 796+180" at DPWH. A printed
       inventory sheet that labels a position in a notation the office does not
       use is a sheet somebody re-types. */
    [vocab().chainage, chainage(b.km) ?? NOT_RECORDED],
    [vocab().area, shown(b.district)],
    [vocab().region, shown(b.province)],
    ['Watercourse crossed', shown(b.river)],
    [vocab().office, b.divisionOffice ?? NOT_RECORDED],
    [
      'Position',
      b.lat != null && b.lng != null
        ? `${b.lat.toFixed(6)}, ${b.lng.toFixed(6)}${provenanceOf(b, 'lat').mark ? ` (${provenanceOf(b, 'lat').label.toLowerCase()})` : ''}`
        : '—',
    ],
    ['Over / under', b.overUnder ?? '—'],
    ['Left bank location', b.leftBankDistrict ? `${b.leftBankDistrict}${b.leftBankVdc ? `, ${b.leftBankVdc}` : ''}` : '—'],
    ['Right bank location', b.rightBankDistrict ? `${b.rightBankDistrict}${b.rightBankVdc ? `, ${b.rightBankVdc}` : ''}` : '—'],
  ];
}

function structureRows(b: BridgeView): [string, string][] {
  return [
    ['Superstructure', shown(b.type)],
    ['No. of spans', count(b.spans)],
    ['Total length', metres(b.length)],
    ['Span length', metres(b.spanLength)],
    ['Bridge width', metres(b.deckWidth)],
    ['Carriageway', metres(b.carriageway)],
    ['Deck level', b.deckRL != null ? `${b.deckRL.toFixed(2)} m` : '—'],
    ['HFL', b.hfl != null ? `${b.hfl.toFixed(2)} m` : '—'],
    ['Freeboard', b.freeboard != null ? `${b.freeboard.toFixed(2)} m` : '—'],
    ['Abutment type', b.abutmentType ?? '—'],
    ['Abutment foundation', b.abutmentFoundation ?? '—'],
    ['Pier type', b.pierType ?? '—'],
    ['Pier foundation', b.pierFoundation ?? '—'],
    ['Bearings', b.bearings ?? '—'],
    [
      'Deck bearing',
      b.deckBearing != null
        ? `${b.deckBearing.toFixed(1)}\u00b0${provenanceOf(b, 'deckBearing').mark ? ` (${provenanceOf(b, 'deckBearing').label.toLowerCase()})` : ''}`
        : '—',
    ],
  ];
}

function serviceRows(b: BridgeView): [string, string][] {
  return [
    ['Railings', b.railings ?? '—'],
    ['Movement', b.movement ?? '—'],
    ['River training', b.riverTraining ?? '—'],
    ['Running surface', b.runningSurface ?? '—'],
    ['Road signs', b.roadSigns ?? '—'],
    ['Services', b.services ?? '—'],
    ['Load restriction', b.loadRestriction != null ? `${b.loadRestriction} T` : '—'],
    ['Height restriction', b.heightRestriction != null ? `${b.heightRestriction} m` : '—'],
    ['Design load', b.designLoad ?? '—'],
  ];
}

/**
 * What a closure would cost, on the printed sheet.
 *
 * Built from the same function the record uses, so the sheet cannot drift from
 * the screen. The notes are dropped here and the values are not: a printed sheet
 * that repeated "nobody has recorded whether emergency access depends on this
 * crossing" five times would be unreadable, and the em dash already says it.
 * The one note kept is the age of a traffic count, which changes what the
 * number means.
 */
function consequenceRows(b: BridgeView): [string, string][] {
  return consequenceFacts(b).map((f) => [f.label, f.value] as [string, string]);
}

function historyRows(b: BridgeView): [string, string][] {
  return [
    ['Designed by', b.designedBy ?? '—'],
    ['Constructed by', b.constructedBy ?? '—'],
    ['Year of completion', count(b.year)],
  ];
}

/**
 * A two-column field list.
 *
 * An em dash means the source document does not record the field, never zero —
 * and it is set in a muted italic so a reader scanning the sheet can see at a
 * glance how much of it is actually known.
 */
function fieldTable(rows: [string, string][]): HTMLElement {
  return h(
    'table',
    { class: 'findings' },
    h(
      'tbody',
      {},
      ...rows.map(([k, v]) =>
        h(
          'tr',
          {},
          h('td', { style: 'width:46%', text: k }),
          h('td', {
            class: ['num', v === '—' && 'fact__v--none'],
            style: 'text-align:right',
            text: v,
          }),
        ),
      ),
    ),
  );
}

/**
 * The screening, as a section of the printed sheet.
 *
 * Every band carries its inputs on the record; on paper it carries the sentence
 * instead. A sheet is a document somebody quotes from, so the reasoning has to
 * travel with the verdict — a printed "High" with nothing beside it is precisely
 * the number that gets copied into a report as though it were an assessment.
 */
function screeningSection(b: BridgeView): HTMLElement {
  const screening = screenBridge(b);
  return h(
    'div',
    {},
    h(
      'table',
      { class: 'findings' },
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { style: 'width:96px', text: 'Screen' }),
          h('th', { style: 'width:104px', text: 'Band' }),
          h('th', { text: 'Basis' }),
        ),
      ),
      h(
        'tbody',
        {},
        ...screening.facets.map((f) =>
          h(
            'tr',
            {},
            h('td', { text: f.label }),
            h('td', {
              class: 'nowrap',
              style: f.band === 'unknown' ? 'color:var(--txt3)' : 'font-weight:600',
              text: bandLabel(f.band),
            }),
            h('td', { text: f.reason }),
          ),
        ),
        h(
          'tr',
          {},
          h('td', { style: 'font-weight:600', text: 'Overall' }),
          h('td', {
            class: 'nowrap',
            style: screening.overall === 'unknown' ? 'color:var(--txt3)' : 'font-weight:600',
            text: bandLabel(screening.overall),
          }),
          h('td', { text: 'Worst of the three' }),
        ),
      ),
    ),
  );
}

/* --------------------------- screen --------------------------- */

/**
 * Condition per element on the printed sheet.
 *
 * One line rather than the record's grid: the sheet is a document, and a table
 * inside a table cell reads badly on paper. Elements the visit did not assess
 * are absent here as on the record.
 */
function reportElements(insp: BridgeView['inspections'][number]): HTMLElement | null {
  const entries = insp.elements ?? [];
  if (!entries.length) return null;

  const max = scaleMax(insp.scale);
  const parts = entries.map(
    (e) => `${elementLabel(e.element)} ${e.rating}${max === null ? '' : `/${max}`}`,
  );

  return h('div', {
    style: 'margin-top:4px;font-size:10.5px;color:var(--txt3)',
    text: `Element condition — ${parts.join(' · ')}`,
  });
}

/**
 * Photographs from one inspection, on the printed sheet.
 *
 * Plain images rather than the buttons the record uses: this is a document, and
 * nothing on it is clickable once it is on paper. `print-color-adjust` in the
 * print rules already keeps images from being dropped by the browser's
 * ink-saving pass.
 */
function reportThumbs(bridgeId: string, insp: BridgeView['inspections'][number]): HTMLElement | null {
  const photos = insp.photos ?? [];
  if (!photos.length) return null;

  return h(
    'span',
    { class: 'insp-thumbs' },
    ...photos.map((photo, i) =>
      h(
        'span',
        { class: 'insp-thumbs__btn' },
        h('img', {
          src: api.inspectionPhotoUrl(bridgeId, photo.id),
          alt: `Photograph ${i + 1} from the inspection of ${prettyDate(insp.date)}`,
        }),
      ),
    ),
  );
}

/** How many plates the sheet prints. Two across, the width of the column. */
const PLATE_LIMIT = 2;

/**
 * Plates for section 8 — photographs of the structure, printed beside the notes.
 *
 * Photographs the record actually holds, never a slot to drop one into. Two empty
 * grey frames on a signed sheet read as a plate that failed to print rather than
 * as a bridge nobody has photographed, so when there is nothing to draw this
 * draws nothing and the notes take the width.
 *
 * Defect photographs first: those are on the record, with an author and a date.
 * Failing those, whatever was dropped on the record's own photo slots — session
 * previews that die on reload, and captioned as such so nobody quotes one as
 * evidence held on file.
 */
function reportPlates(b: BridgeView, s: Readonly<State>): HTMLElement | null {
  const photographed = s.registerDefects.filter((d) => d.bridgeId === b.id && d.hasPhoto);

  const plates = photographed.length
    ? photographed
        .flatMap((d) => {
          /* Where, in whatever precision was recorded — the model component, else
             the coarse DoR element, else nothing. A plate caption on a signed sheet
             has to be honest about which of the three it is. */
          const where = d.component ?? (d.element ? elementLabel(d.element) : 'location not recorded');
          const count = defectPhotoCount(d);
          return Array.from({ length: count }, (_, i) => {
            const what =
              `${where} · ${defectCodeLabel(d.code)}` + (count > 1 ? ` · ${i + 1}/${count}` : '');
            return {
              src: api.defectPhotoSrc(d, i + 1),
              caption: what,
              alt: `${what} — ${d.severity}${d.note ? `. ${d.note}` : ''}`,
            };
          });
        })
        .slice(0, PLATE_LIMIT)
    : Array.from({ length: 3 }, (_, i) => slotImage(`det-${b.id}-p${i + 1}`))
        .filter((url): url is string => Boolean(url))
        .slice(0, PLATE_LIMIT)
        .map((src) => ({
          src,
          caption: 'attached this session · not on the record',
          alt: `Photograph attached to ${b.name} this session`,
        }));

  if (!plates.length) return null;

  const rest = photographed.length - plates.length;

  return h(
    'div',
    {},
    h(
      'div',
      { class: 'plates' },
      ...plates.map((plate, i) =>
        h(
          'figure',
          { class: 'plate' },
          h(
            'div',
            { class: 'plate__frame' },
            h('img', {
              src: plate.src,
              alt: plate.alt,
              loading: 'lazy',
              /* As on the record: bytes that will not decode leave the caption and
                 an empty frame rather than a broken-image glyph. */
              onerror: (e: Event) => (e.currentTarget as HTMLElement).classList.add('plate__img--gone'),
            }),
          ),
          h('figcaption', { class: 'plate__cap', text: `Plate ${i + 1} — ${plate.caption}` }),
        ),
      ),
    ),
    /* What the sheet is not showing. A page that printed the first two of eleven
       photographs without saying so reads as the whole of the evidence. */
    rest > 0
      ? h('div', {
          class: 'plate__more',
          text: `${rest} further photograph${rest === 1 ? '' : 's'} in the issue log.`,
        })
      : null,
  );
}

export function reportScreen(s: Readonly<State>): HTMLElement {
  const b = findBridge(s.selectedId);
  /* No structure, no sheet. `findBridge` is null only on an empty register, and
     an inventory sheet printed from no record would be a government form with a
     dash in every box — which is worse than a page that says there is nothing to
     print. */
  if (b === null) return emptyState('This register has no structures in it yet.');
  const standing = inspectionStanding(b);
  const history = b.inspections.slice().sort((x, y) => (x.date < y.date ? 1 : -1));
  const plates = reportPlates(b, s);

  const sheet = h(
    'div',
    { class: 'report' },

    /* --- masthead --- */
    h(
      'div',
      { class: 'report__masthead' },
      h(
        'div',
        {},
        /* THE SOVEREIGN FROM THE AGENCY BLOCK. "Government of Nepal" was
           hard-coded here — on the masthead of a printed inventory sheet, the
           one artefact of this application that goes into a physical file. A
           DPWH sheet headed with another country's government is not a
           cosmetic defect. */
        h('div', { class: 'report__agency', text: `${agency().government} · ${agency().ministry}` }),
        h('h2', { class: 'report__dept', text: `${agency().department} — ${agency().branch}` }),
        h('div', { style: 'font-size:12px;color:var(--txt2)', text: `Bridge Inventory Sheet · ${agency().unit}` }),
      ),
      h(
        'div',
        { class: 'report__issue' },
        h('div', {}, 'Asset id ', h('strong', { text: b.id })),
        h('div', { text: `Sheet issued ${longDate(new Date())}` }),
        h('div', {
          text: s.backend === 'live' ? 'Source: D1 register' : 'Source: bundled seed (offline)',
        }),
      ),
    ),

    /* --- subject --- */
    h(
      'div',
      { class: 'report__head' },
      h(
        'div',
        { style: 'min-width:0' },
        h('div', { class: 'report__title', text: b.name }),
        h('div', {
          style: 'font-size:13px;color:var(--txt2)',
          /* THE SHEET'S OWN SUBHEADING, in the tenant's words and with the
             clauses it has no value for dropped. "District" and "Province" are
             appended to the values rather than hard-coded around them because
             DPWH reads them as District Engineering Office and Region — see
             `vocab` in src/tenant/types.ts — and because a sheet printing
             "null District, null Province" is a sheet somebody retypes. */
          text: join(
            [
              phrase([b.route, b.roadName], ' '),
              chainage(b.km),
              phrase(
                [
                  b.district == null ? null : `${b.district} ${vocab().area}`,
                  b.province == null ? null : `${b.province} ${vocab().region}`,
                ],
                ', ',
              ),
              b.river == null ? null : `crossing ${b.river}`,
            ],
            ' · ',
          ),
        }),
      ),
    ),

    /* --- 1 & 2 --- */
    h(
      'div',
      { class: 'report__cols' },
      h(
        'div',
        {},
        h('div', { class: 'report__h', text: '1 · Location and road' }),
        fieldTable(locationRows(b)),
      ),
      h(
        'div',
        {},
        h('div', { class: 'report__h', text: '2 · Structure' }),
        fieldTable(structureRows(b)),
      ),
    ),

    /* --- 3 & 4 --- */
    h(
      'div',
      { class: 'report__cols' },
      h(
        'div',
        {},
        h('div', { class: 'report__h', text: '3 · Furniture and restrictions' }),
        fieldTable(serviceRows(b)),
      ),
      h(
        'div',
        {},
        h('div', { class: 'report__h', text: '4 · History' }),
        fieldTable(historyRows(b)),
        h('div', { class: 'report__h', text: '5 · Consequence of closure' }),
        fieldTable(consequenceRows(b)),
      ),
    ),

    /* --- 6 · inspections --- */
    h('div', { class: 'report__h', text: '6 · Inspection record' }),
    history.length
      ? h(
          'table',
          { class: 'findings' },
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { style: 'width:96px', text: 'Date' }),
              h('th', { style: 'width:130px', text: 'Type' }),
              h('th', { class: 'r', style: 'width:80px', text: 'Rating' }),
              h('th', { style: 'width:74px', text: 'Scale' }),
              h('th', { style: 'width:78px', text: 'State' }),
              h('th', { text: 'Observation' }),
            ),
          ),
          h(
            'tbody',
            {},
            ...history.map((insp) =>
              h(
                'tr',
                {},
                h('td', { class: 'num nowrap', text: prettyDate(insp.date) }),
                h('td', { text: inspectionTypeLabel(insp.type) }),
                h('td', { class: 'r num', text: String(insp.rating) }),
                h('td', { class: 'num', text: insp.scale }),
                /* An entry nobody has accepted must say so on paper. This sheet
                   gets carried to the structure and quoted from; a draft
                   printed indistinguishably from a signed-off record is the
                   whole failure GR-07 exists to stop. An entry that predates
                   the workflow reads as neither. */
                h('td', {
                  class: 'num nowrap',
                  style: insp.state && insp.state !== 'accepted' ? 'font-weight:600' : 'color:var(--txt3)',
                  text: !insp.state
                    ? 'on file'
                    : insp.state === 'accepted'
                      ? 'accepted'
                      : insp.state === 'review'
                        ? 'IN REVIEW'
                        : 'DRAFT',
                }),
                /* The observation, and the photographs taken at that visit
                   beneath it. On the printed sheet they are the evidence for
                   the sentence above them, so they belong in the same cell
                   rather than in a plate section further down. */
                h(
                  'td',
                  {},
                  h('span', { text: insp.note }),
                  reportElements(insp),
                  reportThumbs(b.id, insp),
                ),
              ),
            ),
          ),
        )
      : h('div', { class: 'reg__empty', style: 'text-align:left;padding:14px 0', text: 'No inspections on file.' }),

    h('div', {
      style: 'font-size:11.5px;color:var(--txt3);margin-top:8px',
      /* Both cycles on the printed sheet. It is the document somebody carries
         to the structure, and "next inspection due" meaning only the routine
         one is exactly the ambiguity this stopped being able to afford. */
      text:
        `${cycleLabel(standing.routine)}. ${cycleLabel(standing.principal)}. ` +
        `As at ${longDate(new Date())}.`,
    }),

    /* --- 7 · screening --- */
    h('div', { class: 'report__h', text: '7 · Hazard screening' }),
    screeningSection(b),

    /* --- 8 · notes and plates --- */
    h('div', { class: 'report__h', text: '8 · Notes and plates' }),
    plates
      ? h(
          'div',
          { class: 'report__cols', style: 'margin-top:12px' },
          h('div', { class: 'report__remarks', text: '' }),
          plates,
        )
      : /* No plates: the ruled space for handwritten notes takes the sheet, rather
           than half of it with empty frames in the other half. */
        h('div', { class: 'report__remarks', style: 'margin-top:12px', text: '' }),

    /* --- sign-off --- */
    h(
      'div',
      { class: 'signoff' },
      h('div', { class: 'signoff__cap', text: 'Prepared by · Division Engineer' }),
      h('div', { class: 'signoff__cap', text: 'Checked by · Senior Divisional Engineer' }),
      h('div', { class: 'signoff__cap', text: `Approved by · ${agency().branch}` }),
    ),
  );

  /* On paper (src/components/paper.ts), under the department's head and over
     the foot of every sheet. */
  return paper({ title: 'Bridge inventory sheet', detail: `${b.id} · ${b.name}`, from: 'Bridge inspection' }, sheet);
}
